import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["select"];

  static values = {
    selectText: { type: String, default: "Select ..." },
    selectAllText: { type: String, default: "Select All" },
    unselectAllText: { type: String, default: "Unselect All" },
    plusAdditionalItemsText: {
      type: String,
      default: "%{first} (+%{additional_count} more)",
    },
  };

  connect() {
    this.addSelectAllControls();
    this.updateDisplay();
  }

  addSelectAllControls() {
    if (this.element.querySelector(".multiselect-controls")) {
      return;
    }

    const label = this.element.querySelector("label");
    if (!label) {
      return;
    }

    let flexWrapper = this.element.querySelector(".multiselect-flex-wrapper");
    if (!flexWrapper) {
      flexWrapper = document.createElement("div");
      flexWrapper.className =
        "multiselect-flex-wrapper flex justify-between items-center";

      label.parentNode.insertBefore(flexWrapper, label);
      flexWrapper.appendChild(label);
    }

    const controlsDiv = document.createElement("div");
    controlsDiv.className = "multiselect-controls";

    controlsDiv.style.display = "flex";
    controlsDiv.style.gap = "0.5rem";

    controlsDiv.innerHTML = `
    <button type="button" 
            class="multiselect-btn select-all-btn text-sm font-medium text-gray-700" 
            data-action="click->multiselect#selectAll">
      ${this.selectAllTextValue}
    </button>
    <button type="button" 
            class="multiselect-btn unselect-all-btn text-sm font-medium text-gray-700"
            data-action="click->multiselect#unselectAll">
      ${this.unselectAllTextValue}
    </button>
  `;

    flexWrapper.appendChild(controlsDiv);
  }

  selectAll() {
    const instance = this.selectTarget.multiSelectInstance;
    if (!instance) return;

    const allItems = this.selectTarget.querySelectorAll(".si-item");
    allItems.forEach((item) => {
      if (!item.classList.contains("si-selected")) {
        item.classList.add("si-selected");
        const key = parseInt(item.dataset.key, 10);
        const itemData = instance.options.items.get(key);
        if (itemData) {
          itemData.selected = true;
          instance.options.items.set(key, itemData);

          // eslint-disable-next-line no-underscore-dangle
          instance._trigger("change", itemData);
        }
      }
    });

    this.updateDisplay();
    this.updateButtonStates();
  }

  unselectAll() {
    const instance = this.selectTarget.multiSelectInstance;
    if (!instance) return;

    const allItems = this.selectTarget.querySelectorAll(".si-item");
    allItems.forEach((item) => {
      if (item.classList.contains("si-selected")) {
        item.classList.remove("si-selected");
        const key = parseInt(item.dataset.key, 10);
        const itemData = instance.options.items.get(key);
        if (itemData) {
          itemData.selected = false;
          instance.options.items.set(key, itemData);

          // eslint-disable-next-line no-underscore-dangle
          instance._trigger("change", itemData);
        }
      }
    });

    this.updateDisplay();
    this.updateButtonStates();
  }

  updateDisplay() {
    const selectedItems = this.selectTarget.querySelectorAll(
      ".si-item.si-selected"
    );
    const resultElement = this.selectTarget.querySelector(".si-result");

    if (!resultElement) return;

    const selectedCount = selectedItems.length;

    if (selectedCount === 0) {
      resultElement.textContent = this.selectTextValue;
    } else if (selectedCount === 1) {
      resultElement.textContent = selectedItems[0].textContent.trim();
    } else {
      resultElement.textContent = this.plusAdditionalItemsTextValue
        .replace("%{first}", selectedItems[0].textContent.trim())
        .replace("%{additional_count}", selectedCount - 1);
    }
  }

  updateButtonStates() {
    const controlsContainer = this.element.querySelector(
      ".multiselect-controls"
    );
    if (!controlsContainer) return;

    const selectAllBtn = controlsContainer.querySelector(".select-all-btn");
    const unselectAllBtn = controlsContainer.querySelector(".unselect-all-btn");

    const allItems = this.selectTarget.querySelectorAll(".si-item");
    const selectedItems = this.selectTarget.querySelectorAll(
      ".si-item.si-selected"
    );

    const totalCount = allItems.length;
    const selectedCount = selectedItems.length;

    if (selectedCount === 0) {
      selectAllBtn.disabled = totalCount === 0;
      unselectAllBtn.disabled = true;
    } else if (selectedCount === totalCount && totalCount > 0) {
      selectAllBtn.disabled = true;
      unselectAllBtn.disabled = false;
    } else {
      selectAllBtn.disabled = false;
      unselectAllBtn.disabled = false;
    }
  }

  setSelectedItems(keys) {
    const items = this.selectTarget.querySelectorAll(".si-item");
    items.forEach((item) => {
      const itemKey = item.dataset.key;
      if (keys.includes(itemKey)) {
        item.classList.add("si-selected");
      } else {
        item.classList.remove("si-selected");
      }
    });

    this.updateDisplay();
    this.updateButtonStates();
  }

  get selectedValues() {
    const selectedItems = this.selectTarget.querySelectorAll(
      ".si-item.si-selected"
    );
    return Array.from(selectedItems).map((item) => item.dataset.key);
  }

  get selectedItems() {
    const selectedItems = this.selectTarget.querySelectorAll(
      ".si-item.si-selected"
    );
    return Array.from(selectedItems).map((item) => ({
      key: item.dataset.key,
      value: item.textContent.trim(),
    }));
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}
